/**
 * Phase 2 — Centralized data sufficiency.
 *
 * Every Parent Hub card should respect this single derivation instead of
 * re-deriving "is there enough data?" locally. Also gates whether the AI
 * Parent Briefing pathway is allowed to spend tokens on this child.
 */

import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";

export type DataSufficiencyLevel = "empty" | "low" | "ready";

export interface DataSufficiency {
  level: DataSufficiencyLevel;
  /** Whether the AI summary pipeline is allowed to call the model. */
  aiEligible: boolean;
  /** Whether quiz-driven signals (scores, pass rate) are statistically meaningful. */
  quizSignalReady: boolean;
  /** Whether at least some learning is recorded. */
  hasLearningSignal: boolean;
  /** Short human-readable reason for the current level. */
  reason: string;
}

export function deriveDataSufficiency(a: ParentAnalyticsResult): DataSufficiency {
  const lessonsTotal = a.lessonActivity.length;
  const quizzesTotal = a.quizAttempts.length;
  const hasLearningSignal = lessonsTotal > 0 || quizzesTotal > 0;
  const quizSignalReady = quizzesTotal >= 3;

  if (!hasLearningSignal) {
    return {
      level: "empty",
      aiEligible: false,
      quizSignalReady: false,
      hasLearningSignal: false,
      reason: "No lessons or quizzes recorded yet.",
    };
  }
  // Need a meaningful blend before generating an AI briefing.
  if (lessonsTotal < 2 && !quizSignalReady) {
    return {
      level: "low",
      aiEligible: false,
      quizSignalReady,
      hasLearningSignal: true,
      reason: "Some early activity — needs more lessons or quizzes for a meaningful briefing.",
    };
  }
  return {
    level: "ready",
    aiEligible: true,
    quizSignalReady,
    hasLearningSignal: true,
    reason: "Enough activity to generate a meaningful parent briefing.",
  };
}